import { Flex, FormGroup, FormLabel, Grid } from "@eworkit/iris";
import { Formik, FormikHelpers, FormikProps } from "formik";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { FormikTextInput, VALIDATION_TYPES } from "@/components/Formik";
import { FormikError } from "@/components/Formik";
import { FormikForm } from "@/formikComponents";
import {
  PasswordValidation,
  passwordValidationSchema,
  usePasswordValidation,
} from "@/views/components/Forms/PasswordValidation";
import { Messenger } from "@/views/components/Messenger";

export const PASSWORD_FORM_MESSENGER_CONTEXT = "password-form-context";

export interface IPasswordFormValues {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

const validationSchema = Yup.object().shape({
  oldPassword: Yup.string().required(VALIDATION_TYPES.REQUIRED),
  newPassword: Yup.string().concat(passwordValidationSchema),
  confirmNewPassword: Yup.string()
    .required(VALIDATION_TYPES.REQUIRED)
    .oneOf([Yup.ref("newPassword"), ""], VALIDATION_TYPES.FIELDS_NOT_MATCH),
});

interface IPasswordFormProps extends IEditableSectionRenderProps {
  form: FormikProps<IPasswordFormValues>;
}

export const PasswordForm: FC<IPasswordFormProps> = ({ form, editableProps }) => {
  const { t } = useTranslation("session");

  const passwordErrors = usePasswordValidation(form.values.newPassword);

  useEditableSubmit(form, editableProps);

  return (
    <FormikForm>
      <Flex flexDirection="column" gap="md" flexGrow={1} mt={["xl", null, null, "0"]}>
        <Messenger context={PASSWORD_FORM_MESSENGER_CONTEXT} />

        <Grid columns={["1fr", null, "1fr 1fr", "1fr 1fr 1fr"]} gap="md">
          <Flex flexGrow={1} flexDirection="column">
            <FormGroup
              label={<FormLabel required label={t("USERS.SETTINGS.USER_ACCOUNT.PASSWORD.CURRENT_PASSWORD")} />}
              error={<FormikError name="oldPassword" />}>
              <FormikTextInput size="md" value={form.values.oldPassword} type="password" name="oldPassword" required />
            </FormGroup>
          </Flex>
        </Grid>

        <Grid columns={["1fr", null, "1fr 1fr", "1fr 1fr 1fr"]} gap="md">
          <Flex flexGrow={1} flexDirection="column">
            <FormGroup
              label={<FormLabel required label={t("USERS.SETTINGS.USER_ACCOUNT.PASSWORD.NEW_PASSWORD")} />}
              error={
                <FormikError
                  name="newPassword"
                  overrideMessages={{
                    PASSWORD_BAD_CHAR: t("SESSION_PASSWORD_SETUP.ERROR_MESSAGES.PASSWORD_BAD_CHAR"),
                  }}
                />
              }>
              <FormikTextInput size="md" value={form.values.newPassword} type="password" name="newPassword" required />
            </FormGroup>
          </Flex>

          <Flex flexGrow={1} flexDirection="column">
            <FormGroup
              label={<FormLabel required label={t("USERS.SETTINGS.USER_ACCOUNT.PASSWORD.CONFIRM_NEW_PASSWORD")} />}
              error={<FormikError name="confirmNewPassword" />}>
              <FormikTextInput
                size="md"
                value={form.values.confirmNewPassword}
                type="password"
                name="confirmNewPassword"
                required
              />
            </FormGroup>
          </Flex>
        </Grid>

        <Grid columns={["1fr", null, "1fr 1fr", "1fr 1fr 1fr"]} gap="md">
          <Flex flexGrow={1} flexDirection="column">
            <PasswordValidation errors={passwordErrors} />
          </Flex>
        </Grid>
      </Flex>
    </FormikForm>
  );
};

interface IPasswordFormikProps extends IPasswordFormProps {
  initialValues: IPasswordFormValues;
  onSubmit: (values: IPasswordFormValues, actions: FormikHelpers<IPasswordFormValues>) => Promise<void>;
}

export const PasswordFormik: FC<Omit<IPasswordFormikProps, "form">> = ({ initialValues, onSubmit, ...rest }) => (
  <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
    {(form: FormikProps<IPasswordFormValues>) => <PasswordForm {...rest} form={form} />}
  </Formik>
);
